import {
  ALL_FONT_SIZE_RATIOES,
  FontSizeRatio,
} from "@shared/components/layerPopups/FontSetting"

import Cookies from "js-cookie"
import { v4 as uuidv4 } from "uuid"

function setFont(size: FontSizeRatio): void {
  Cookies.set(Font.SIZE, size, { expires: 365, sameSite: "Strict" })
}

function getFont(): FontSizeRatio | null {
  const fontSizeRatio = Cookies.get(Font.SIZE) as FontSizeRatio

  if (Object.values(ALL_FONT_SIZE_RATIOES).includes(fontSizeRatio))
    return fontSizeRatio

  return null
}

function setManagerFont(
  size: FontSizeRatio,
  color: string,
  isBold: boolean,
): void {
  Cookies.set(ManagerFont.SIZE, size, {
    expires: 365,
    sameSite: "Strict",
  })
  Cookies.set(ManagerFont.COLOR, color, { expires: 365 })
  Cookies.set(ManagerFont.IS_BOLD, isBold.toString(), { expires: 365 })
}

function getManagerFontSize(): FontSizeRatio | null {
  const managerFontSizeRatio = Cookies.get(ManagerFont.SIZE) as FontSizeRatio

  if (Object.values(ALL_FONT_SIZE_RATIOES).includes(managerFontSizeRatio))
    return managerFontSizeRatio

  return null
}

function getManagerFontColor(): string {
  const managerFontColor = Cookies.get(ManagerFont.COLOR) || "#B80000"

  if (!/^#[0-9A-F]{6}$/i.test(managerFontColor)) return "#B80000"

  return managerFontColor
}

function getManagerFontIsBold(): boolean {
  return Cookies.get(ManagerFont.IS_BOLD) === "true"
}

function setDisplay(displayRatio: DisplayRatio): void {
  Cookies.set(Display.RATIO, displayRatio, {
    expires: 365,
    sameSite: "Strict",
  })
}

function getDisplay(): DisplayRatio | null {
  const displayRatio = Cookies.get(Display.RATIO) as DisplayRatio

  if (ALL_DISPLAY_RATIOES.includes(displayRatio)) return displayRatio

  return null
}

function setChatSound(chatSound: ChatSound): void {
  Cookies.set(Chat.SOUND, chatSound, { expires: 365, sameSite: "Strict" })
}

function getChatSound(): ChatSound {
  if ((Cookies.get(Chat.SOUND) as ChatSound) === "ON") {
    return "ON"
  }

  return "OFF"
}

function setPlayerVolume(volume: string): void {
  Cookies.set(Player.VOLMUE, volume, { sameSite: "Strict", expires: 365 })
}

function getPlayerVolume(): string | undefined {
  return Cookies.get(Player.VOLMUE)
}

function setPlayerMute(isMute: boolean): void {
  Cookies.set(Player.MUTE, isMute ? "on" : "off", { sameSite: "Strict" })
}

function getPlayerMute(): string | undefined {
  return Cookies.get(Player.MUTE)
}

function setIsPopupClosed(popup: string | number): void {
  Cookies.set(`${CookieConfig.KEY}.${popup}`, "closed", { expires: 7 })
}

function getIsPopupClosed(popup: string | number): string | undefined {
  return Cookies.get(`${CookieConfig.KEY}.${popup}`)
}

function setUuid(): string {
  const uuid = uuidv4()

  Cookies.set(`${CookieConfig.KEY}.uuid`, uuid, { expires: 365 })

  return uuid
}

function getUuid(): string | undefined {
  return Cookies.get(`${CookieConfig.KEY}.uuid`)
}

function getCookies(key: string) {
  const prefix = getCookiesKey()
  return Cookies.get(prefix ? `${prefix}.${key}` : key)
}

function setCookies(key: string, value: string) {
  const prefix = getCookiesKey()
  Cookies.set(prefix ? `${prefix}.${key}` : key, value)
}

function removeCookies(key: string) {
  const prefix = getCookiesKey()
  Cookies.remove(prefix ? `${prefix}.${key}` : key)
}

export {
  setFont,
  getFont,
  setManagerFont,
  getManagerFontSize,
  getManagerFontColor,
  getManagerFontIsBold,
  setDisplay,
  getDisplay,
  setChatSound,
  getChatSound,
  setPlayerVolume,
  getPlayerVolume,
  setPlayerMute,
  getPlayerMute,
  setIsPopupClosed,
  getIsPopupClosed,
  setUuid,
  getUuid,
  getCookies,
  setCookies,
  removeCookies,
}

const CookieConfig = {
  KEY: `mpro.wownet.${getCookiesKey()}`,
}

const Player = {
  VOLMUE: "playerVolume",
  MUTE: "playerMute",
}

const Font = {
  SIZE: "fontSize",
}

const Chat = {
  SOUND: "chatSound",
}

const ManagerFont = {
  IS_BOLD: "managerFontIsBold",
  SIZE: "managerFontSize",
  COLOR: "managerFontColor",
}

const Display = {
  RATIO: "displayRatio",
}

function getCookiesKey() {
  const isVod = window.location.pathname.includes("/vods")

  return isVod ? "vods" : ""
}

export type ChatSound = "ON" | "OFF"

const ALL_DISPLAY_RATIOES = [
  "originalRatio",
  "centerRatio",
  "displayRatio",
] as const
type DisplayRatioTuple = typeof ALL_DISPLAY_RATIOES
export type DisplayRatio = DisplayRatioTuple[number]
